import { logger } from '../utils/logger';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import FollowupAlerts from '../components/FollowupAlerts';
import { 
  Dog, 
  Cat, 
  Plus, 
  Activity, 
  Brain, 
  FileText,
  ArrowRight,
  Clock
} from 'lucide-react';

import { BACKEND_URL } from '../config/api';

const Dashboard = () => {
  const { user } = useAuth();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, dogs: 0, cats: 0, recentExams: 0 });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/cases/patients?limit=100`, {
          withCredentials: true
        });
        const items = data.items || data; // Support both paginated and legacy format
        setPatients(items);
        
        // Calculate stats
        const dogs = items.filter(p => p.species === 'dog').length;
        const cats = items.filter(p => p.species === 'cat').length;
        setStats({
          total: data.total || items.length,
          dogs,
          cats,
          recentExams: items.length // Simplified for now
        });
      } catch (error) {
        logger.error('Error fetching patients:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const recentPatients = patients.slice(0, 5);

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-[#2A2C2A] font-['Work_Sans']">
            Welcome back, {user?.name}
          </h1>
          <p className="text-[#5C5F5C] mt-1">Here's what's happening with your cases</p>
        </div>
        <Link to="/cases/new">
          <Button className="bg-[#4B6651] hover:bg-[#3E5543] text-white" data-testid="new-case-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Patient
          </Button>
        </Link>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Patients */}
        <Card className="lg:col-span-2 border-[#E5E4E0] bg-white" data-testid="recent-patients">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-['Work_Sans']">Recent Patients</CardTitle>
            <Link to="/cases" className="text-sm text-[#4B6651] hover:text-[#3E5543] flex items-center gap-1">
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-16 bg-[#F8F7F4] rounded-lg animate-pulse" />
                ))}
              </div>
            ) : recentPatients.length > 0 ? (
              <div className="space-y-3">
                {recentPatients.map((patient) => (
                  <Link
                    key={patient.id}
                    to={`/cases/${patient.id}`}
                    className="flex items-center gap-4 p-4 rounded-lg border border-[#E5E4E0] hover:border-[#4B6651] hover:bg-[#F8F7F4] transition-all duration-200"
                    data-testid={`patient-${patient.id}`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      patient.species === 'dog' ? 'bg-[#C06144]/10' : 'bg-[#4B6651]/10'
                    }`}>
                      {patient.species === 'dog' ? (
                        <Dog className="w-5 h-5 text-[#C06144]" />
                      ) : (
                        <Cat className="w-5 h-5 text-[#4B6651]" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#2A2C2A] truncate">{patient.name}</p>
                      <p className="text-sm text-[#5C5F5C] truncate">
                        {patient.breed} • {patient.age}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-[#5C5F5C]">
                      <Clock className="w-3 h-3" />
                      {new Date(patient.created_at).toLocaleDateString()}
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <img
                  src="https://images.pexels.com/photos/6816851/pexels-photo-6816851.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=200&w=300"
                  alt="No patients"
                  className="w-32 h-32 object-cover rounded-lg mx-auto mb-4 opacity-80"
                />
                <p className="text-[#5C5F5C] mb-4">No patients yet</p>
                <Link to="/cases/new">
                  <Button variant="outline" className="border-[#4B6651] text-[#4B6651] hover:bg-[#4B6651] hover:text-white">
                    <Plus className="w-4 h-4 mr-2" />
                    Add your first patient
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="border-[#E5E4E0] bg-white" data-testid="quick-actions">
          <CardHeader>
            <CardTitle className="text-lg font-['Work_Sans']">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to="/cases/new" className="block">
              <Button variant="outline" className="w-full justify-start border-[#E5E4E0] hover:border-[#4B6651] hover:bg-[#4B6651]/5">
                <Plus className="w-4 h-4 mr-3 text-[#4B6651]" />
                New Patient Case
              </Button>
            </Link>
            <Link to="/edu-search" className="block">
              <Button variant="outline" className="w-full justify-start border-[#E5E4E0] hover:border-[#4B6651] hover:bg-[#4B6651]/5">
                <Activity className="w-4 h-4 mr-3 text-[#4B6651]" />
                Search .edu Resources
              </Button>
            </Link>
            <Link to="/cases" className="block">
              <Button variant="outline" className="w-full justify-start border-[#E5E4E0] hover:border-[#4B6651] hover:bg-[#4B6651]/5">
                <Brain className="w-4 h-4 mr-3 text-[#4B6651]" />
                View All Analyses
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Follow-up Reminders */}
        <FollowupAlerts />
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="stats-grid">
        <Card className="border-[#E5E4E0] bg-white card-hover" data-testid="stat-total">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5C5F5C] mb-1">Total Patients</p>
                <p className="text-3xl font-semibold text-[#2A2C2A] font-['Work_Sans']">{stats.total}</p>
              </div>
              <div className="w-12 h-12 bg-[#4B6651]/10 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-[#4B6651]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-[#E5E4E0] bg-white card-hover" data-testid="stat-dogs">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5C5F5C] mb-1">Dogs</p>
                <p className="text-3xl font-semibold text-[#2A2C2A] font-['Work_Sans']">{stats.dogs}</p>
              </div>
              <div className="w-12 h-12 bg-[#C06144]/10 rounded-lg flex items-center justify-center">
                <Dog className="w-6 h-6 text-[#C06144]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-[#E5E4E0] bg-white card-hover" data-testid="stat-cats">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5C5F5C] mb-1">Cats</p>
                <p className="text-3xl font-semibold text-[#2A2C2A] font-['Work_Sans']">{stats.cats}</p>
              </div>
              <div className="w-12 h-12 bg-[#4B6651]/10 rounded-lg flex items-center justify-center">
                <Cat className="w-6 h-6 text-[#4B6651]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-[#E5E4E0] bg-white card-hover" data-testid="stat-analyses">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5C5F5C] mb-1">AI Analyses</p>
                <p className="text-3xl font-semibold text-[#2A2C2A] font-['Work_Sans']">{stats.recentExams}</p>
              </div>
              <div className="w-12 h-12 bg-[#4B6651]/10 rounded-lg flex items-center justify-center">
                <Brain className="w-6 h-6 text-[#4B6651]" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
